import { useQuery, type UseQueryOptions } from "@tanstack/react-query"

import type { GID } from "@unlikelystudio/commerce-connector"

import type { Nullish } from "~/@types/generics"
import type { AlgoliaProductHit } from "~/lib/algolia/types"
import { ALGOLIA_RECOMMENDATIONS_PARAMS } from "~/lib/api/constants"
import { requestNextApiEndpoint } from "~/lib/api/utils/request-endpoint"
import useLocale from "~/hooks/useLocale"

type QueryData = AlgoliaProductHit[]
type QueryKey = ["algola", { gid: Nullish<GID> }]

export function useAlgoliaRecommendations(
  gid: Nullish<GID>,
  queryOptions: UseQueryOptions<QueryData, unknown, QueryData, QueryKey>
) {
  const locale = useLocale()

  return useQuery<QueryData, unknown, QueryData, QueryKey>({
    queryKey: ["algola", { gid }],
    queryFn: async () => {
      if (!gid) return []

      try {
        const response = (await requestNextApiEndpoint(ALGOLIA_RECOMMENDATIONS_PARAMS, {
          queryParams: { gid, locale },
        })) as { data: AlgoliaProductHit[] }

        return response?.data ?? []
      } catch {
        return []
      }
    },
    ...queryOptions,
    enabled: Boolean(gid && queryOptions?.enabled),
    staleTime: Infinity,
  })
}
